import { RouterProvider } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { I18nextProvider } from 'react-i18next';

import { startConnectivityWatch } from '../lib/api/connectivity.js';
import { i18n } from '../lib/i18n/index.js';
import { toast } from '../ui/components/toast-store.js';
import { NavigationContext } from '../ui/navigation.js';
import type { AppRouter } from './router.js';
import { trackPageViews } from './tracking/index.js';

// The toast region loads with the first toast: most pages never show one.
const ToastRegion = lazy(() =>
  import('../ui/components/Toast.js').then((module) => ({ default: module.ToastRegion })),
);
const subscribeToToasts = (listener: () => void) => toast.subscribe(listener);
const hasToasts = () => toast.current().length > 0;

function Toasts() {
  const [needed, setNeeded] = useState(false);
  const showing = useSyncExternalStore(subscribeToToasts, hasToasts, () => false);
  if (showing && !needed) setNeeded(true);
  return needed ? (
    <Suspense fallback={null}>
      <ToastRegion />
    </Suspense>
  ) : null;
}

/** Props for `App`. */
export interface AppProps {
  router: AppRouter;
}

/**
 * The app: i18n and navigation providers, the router, the toast region,
 * page-view tracking and the API connectivity watch. React Aria's context
 * and TanStack Query are provided by the route modules that use them
 * (`withAria`, `withQueries`), so pages that need neither never load them.
 */
export function App({ router }: AppProps) {
  useEffect(() => trackPageViews(router).stop, [router]);
  useEffect(() => startConnectivityWatch(), []);
  return (
    <I18nextProvider i18n={i18n}>
      <Providers router={router}>
        <RouterProvider router={router} />
        <Toasts />
      </Providers>
    </I18nextProvider>
  );
}

function Providers({ router, children }: { router: AppRouter; children: React.ReactNode }) {
  const navigation = useMemo(
    () => ({
      preload: (href: string) => {
        void router.preloadRoute({ to: href }).catch(() => undefined);
      },
      navigate: (href: string) => {
        void router.navigate({ to: href });
      },
      href: (to: string) => router.buildLocation({ to }).href,
    }),
    [router],
  );
  return <NavigationContext.Provider value={navigation}>{children}</NavigationContext.Provider>;
}
